import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./styles/SwallowCard.css";

export interface SwallowCardProps {
  icon: React.ReactNode;
  message: string;
  /** CSS selector for the live anchor element. Re-queried at swallow time
   *  so mobile scroll / address-bar collapse / layout shifts don't desync
   *  the destination. */
  anchorSelector: string;
  holdMs?: number;
  onDismiss: () => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Returns true when the anchor is comfortably visible — its centre lies
 *  within the central 70% of the viewport. We use the centre rather than full
 *  containment because the genie targets the centre point. */
function isAnchorInViewport(el: HTMLElement): boolean {
  const rect = el.getBoundingClientRect();
  const cy = rect.top + rect.height / 2;
  const margin = window.innerHeight * 0.15;
  return cy >= margin && cy <= window.innerHeight - margin;
}

// ─── Easing functions ────────────────────────────────────────────────────────

function easeOutExpo(t: number): number {
  return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
}

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

// ─── Genie clip-path animator ─────────────────────────────────────────────────

interface GenieAnimationConfig {
  el: HTMLElement;             // the element to clip
  cardRect: DOMRect;           // current rect of `el`
  targetRect: DOMRect;         // destination — anchor button rect (viewport coords)
  duration: number;
  onComplete: () => void;
}

function runGenieAnimation(cfg: GenieAnimationConfig): () => void {
  const { el, cardRect, targetRect, duration, onComplete } = cfg;
  const start = performance.now();

  const W = cardRect.width;
  const H = cardRect.height;

  // Destination point in `el`'s local coordinate space (clip-path space).
  // clip-path coords are relative to the element's border-box top-left.
  const destX = targetRect.left + targetRect.width / 2 - cardRect.left;
  const destY = targetRect.top + targetRect.height / 2 - cardRect.top;

  // The bottom corners arrive first; the top corners lag by this fraction.
  const TOP_LAG = 0.22;

  let rafId = 0;
  let cancelled = false;

  const tick = (now: number) => {
    if (cancelled) return;
    const elapsed = now - start;
    const tRaw = Math.min(elapsed / duration, 1);

    // Bottom corners — race ahead
    const tBot = easeOutExpo(tRaw);
    // Top corners — lag, then catch up
    const tTopRaw = Math.max(0, (tRaw - TOP_LAG) / (1 - TOP_LAG));
    const tTop = easeOutCubic(tTopRaw);

    // Lerp each corner from its starting position toward (destX, destY)
    const lerp = (from: number, to: number, k: number) => from + (to - from) * k;

    const tlX = lerp(0, destX, tTop);
    const tlY = lerp(0, destY, tTop);
    const trX = lerp(W, destX, tTop);
    const trY = lerp(0, destY, tTop);
    const brX = lerp(W, destX, tBot);
    const brY = lerp(H, destY, tBot);
    const blX = lerp(0, destX, tBot);
    const blY = lerp(H, destY, tBot);

    el.style.clipPath = `polygon(${tlX.toFixed(2)}px ${tlY.toFixed(2)}px, ${trX.toFixed(2)}px ${trY.toFixed(2)}px, ${brX.toFixed(2)}px ${brY.toFixed(2)}px, ${blX.toFixed(2)}px ${blY.toFixed(2)}px)`;

    // Fade out only at the very end so the shape is visible during the genie
    if (tRaw > 0.85) {
      el.style.opacity = String(1 - (tRaw - 0.85) / 0.15);
    }

    if (tRaw < 1) {
      rafId = requestAnimationFrame(tick);
    } else {
      el.style.opacity = "0";
      onComplete();
    }
  };

  rafId = requestAnimationFrame(tick);

  return () => {
    cancelled = true;
    cancelAnimationFrame(rafId);
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

const SwallowCard: React.FC<SwallowCardProps> = ({
  icon,
  message,
  anchorSelector,
  holdMs = 2000,
  onDismiss,
}) => {
  const [phase, setPhase] = useState<"entering" | "shown" | "swallowing" | "done">("entering");
  const shellRef = useRef<HTMLDivElement>(null);
  const scrimRef = useRef<HTMLDivElement>(null);
  const holdTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelGenieRef = useRef<(() => void) | null>(null);

  // ── Phase: entering → shown ──
  // Use plain CSS transition. After entry duration, we move to "shown" and start the hold timer.
  useEffect(() => {
    // Settle delay so the page is rendered before the card appears
    const enterDelay = setTimeout(() => {
      // Trigger the entry transition by switching the className
      if (shellRef.current) shellRef.current.classList.add("swallow-card-shell--shown");
      if (scrimRef.current) scrimRef.current.classList.add("swallow-scrim--shown");

      // After CSS transition completes, mark as "shown" and arm the hold timer
      const enterDoneTimer = setTimeout(() => {
        setPhase("shown");
        holdTimerRef.current = setTimeout(() => startSwallow(), holdMs);
      }, 320);

      return () => clearTimeout(enterDoneTimer);
    }, 600);

    return () => clearTimeout(enterDelay);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startSwallow = () => {
    if (phase === "swallowing" || phase === "done") return;
    if (holdTimerRef.current) clearTimeout(holdTimerRef.current);

    const el = shellRef.current;
    const anchorEl = document.querySelector(anchorSelector) as HTMLElement | null;
    if (!el || !anchorEl) {
      setPhase("done");
      onDismiss();
      return;
    }

    setPhase("swallowing");

    // Fade scrim out so the user can see the page scrolling and the genie
    // flying against the live page underneath.
    if (scrimRef.current) scrimRef.current.classList.remove("swallow-scrim--shown");

    // If the anchor isn't comfortably in view, smooth-scroll it to the centre
    // BEFORE the genie fires — full attention goes to the destination button.
    const needsScroll = !isAnchorInViewport(anchorEl);
    if (needsScroll) {
      anchorEl.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
    }

    // Wait for the scroll to settle, then read the live rect and start the genie.
    // (~480ms for smooth scroll on most browsers; 0 if no scroll needed.)
    const scrollSettleDelay = needsScroll ? 520 : 0;

    const fireGenie = setTimeout(() => {
      const cardRect = el.getBoundingClientRect();
      const targetRect = anchorEl.getBoundingClientRect();

      cancelGenieRef.current = runGenieAnimation({
        el,
        cardRect,
        targetRect,
        duration: 720, // slower for a more deliberate "pulled in" feel
        onComplete: () => {
          setPhase("done");
          onDismiss();
        },
      });
    }, scrollSettleDelay);

    cancelGenieRef.current = () => clearTimeout(fireGenie);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (cancelGenieRef.current) cancelGenieRef.current();
      if (holdTimerRef.current) clearTimeout(holdTimerRef.current);
    };
  }, []);

  if (phase === "done") return null;

  return createPortal(
    <>
      <div
        ref={scrimRef}
        className="swallow-scrim"
        onClick={startSwallow}
      />

      <div
        ref={shellRef}
        className="swallow-card-shell"
        onClick={startSwallow}
      >
        {/* Inner card holds the visible style. Shell receives the clip-path. */}
        <div className="swallow-card">
          <div className="swallow-card__icon-ring">{icon}</div>
          <p className="swallow-card__message">{message}</p>
          <span className="swallow-card__hint">Tap to dismiss</span>
        </div>
      </div>
    </>,
    document.body
  );
};

export default SwallowCard;
